// src/pages/AdminLogin.tsx
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, Loader2, Eye, EyeOff, Lock, User, AlertCircle, CheckCircle } from "lucide-react";

// Get festive or contextual message based on current date
const getGreeting = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const day = now.getDate();
  const hour = now.getHours();

  if (month === 11 && day >= 1 && day <= 26) {
    return {
      icon: "🎄",
      message: "Merry Christmas! Wishing you a productive holiday season.",
      bgColor: "bg-red-50 dark:bg-red-900/20",
      textColor: "text-red-700 dark:text-red-400",
      borderColor: "border-red-200 dark:border-red-800",
    };
  }

  if ((month === 11 && day >= 27) || (month === 0 && day <= 7)) {
    return {
      icon: "🎊",
      message: "Happy New Year! Ready to make 2026 amazing?",
      bgColor: "bg-purple-50 dark:bg-purple-900/20",
      textColor: "text-purple-700 dark:text-purple-400",
      borderColor: "border-purple-200 dark:border-purple-800",
    };
  }

  let timeGreeting = "Good morning";
  let icon = "☀️";

  if (hour >= 12 && hour < 17) {
    timeGreeting = "Good afternoon";
    icon = "🌤️";
  } else if (hour >= 17) {
    timeGreeting = "Good evening";
    icon = "🌙";
  }

  return {
    icon,
    message: `${timeGreeting}! Ready to manage enrollments?`,
    bgColor: "bg-teal-50 dark:bg-teal-900/20",
    textColor: "text-teal-700 dark:text-teal-400",
    borderColor: "border-teal-200 dark:border-teal-800",
  };
};

export default function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const greeting = getGreeting();

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/admin/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError("");

    if (!username || !password) {
      setError("Please enter both username and password");
      return;
    }

    if (username.length < 3) {
      setError("Username must be at least 3 characters");
      return;
    }

    setIsLoading(true);
    try {
      await login(username, password);
      const from = (location.state as any)?.from?.pathname || '/admin/dashboard';
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) handleSubmit();
  };

  return (
    <div className="min-h-screen flex items-center justify-center gradient-turquoise-rich p-4">
      <div className="w-full max-w-2xl animate-in fade-in slide-in-from-bottom-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column: logo + greeting + quick cards */}
          <div className="space-y-6">
            <div className="text-center mb-2">
              <div className="relative inline-block">
                <div className="absolute inset-0 bg-teal-500/20 rounded-2xl blur-xl animate-pulse" />
                <div className="relative w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl">
                  <Shield className="w-10 h-10 text-white" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white">Janet Marketing</h1>
              <p className="text-muted-foreground mt-2">Admin Portal</p>
            </div>

            <Card className={`mb-4 ${greeting.bgColor} border ${greeting.borderColor}`}>
              <CardContent className="p-4">
                <p className={`text-sm ${greeting.textColor} flex items-center gap-2`}>
                  <span className="text-lg">{greeting.icon}</span>
                  {greeting.message}
                </p>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <div className="glass-card p-4 rounded-2xl">
                <h4 className="text-sm font-semibold text-white">Admin Portal</h4>
                <p className="text-xs text-muted-foreground mt-1">Access dashboards and manage enrollments</p>
              </div>

              <div className="glass-card p-4 rounded-2xl">
                <h4 className="text-sm font-semibold text-white">Security</h4>
                <p className="text-xs text-muted-foreground mt-1">Two-factor + session controls</p>
              </div>
            </div>
          </div>

          {/* Right column: Login Card */}
          <div>
            <Card className="glass-card border-0 shadow-2xl">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl font-bold text-center">Welcome Back</CardTitle>
                <CardDescription className="text-center">Sign in to access the admin dashboard</CardDescription>
              </CardHeader>

              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-2">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-sm font-medium">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="username"
                        type="text"
                        placeholder="Enter your username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10 h-12 border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                        disabled={isLoading}
                        autoComplete="username"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="pl-10 pr-10 h-12 border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                        disabled={isLoading}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isLoading}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      <>
                        <Shield className="w-5 h-5 mr-2" />
                        Sign In
                      </>
                    )}
                  </Button>

                  <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-800">
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <CheckCircle className="w-3 h-3" />
                      <span>Secure authentication with JWT</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-400 dark:text-gray-500 mt-2">
                      <Lock className="w-3 h-3" />
                      <span>Session timeout: 8 hours • Auto-refresh enabled</span>
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-8">© 2025 Janet Marketing Academy. All rights reserved.</p>
      </div>
    </div>
  );
}
